import { PyodideStatus, ExecutionResult } from '../types';

type WorkerResultMessage = {
  id: number;
  type: 'result';
  ok: boolean;
  result?: ExecutionResult;
  error?: string;
  stack?: string;
};

type WorkerStatusMessage = {
  type: 'status';
  status: PyodideStatus;
  message?: string;
};

type PendingRequest = {
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
};

let worker: Worker | null = null;
let requestId = 0;
const pending = new Map<number, PendingRequest>();
let initPromise: Promise<void> | null = null;
let statusCallback: ((status: PyodideStatus, message?: string) => void) | null = null;
let busyCount = 0;
let busyCallback: ((busy: boolean) => void) | null = null;

export function onPyodideStatusChange(cb: (status: PyodideStatus, message?: string) => void) {
  statusCallback = cb;
}

export function onPythonBusyChange(cb: (busy: boolean) => void) {
  busyCallback = cb;
}

function setBusy(delta: number) {
  busyCount = Math.max(0, busyCount + delta);
  if (busyCallback) {
    busyCallback(busyCount > 0);
  }
}

function setStatus(status: PyodideStatus, message?: string) {
  if (statusCallback) {
    statusCallback(status, message);
  }
}

function ensureWorker(): Worker {
  if (worker) return worker;

  worker = new Worker('/pyodide-worker.js');

  worker.onmessage = (event: MessageEvent<WorkerResultMessage | WorkerStatusMessage>) => {
    const data = event.data;
    if (!data) return;

    if (data.type === 'status') {
      setStatus(data.status, data.message);
      return;
    }

    if (data.type === 'result') {
      const entry = pending.get(data.id);
      if (!entry) return;
      pending.delete(data.id);
      if (data.ok) {
        entry.resolve(data.result);
      } else {
        entry.reject(new Error(data.error || 'Pyodide worker error'));
      }
    }
  };

  worker.onerror = (err) => {
    console.error('Pyodide worker error:', err);
    setStatus('error', err.message || 'Python worker crashed');
    for (const [, entry] of pending) {
      entry.reject(new Error(err.message || 'Python worker crashed'));
    }
    pending.clear();
    worker = null;
    initPromise = null;
  };

  return worker;
}

function callWorker<T = void>(type: string, payload?: Record<string, unknown>, transfer?: Transferable[]): Promise<T> {
  const w = ensureWorker();
  const id = ++requestId;

  return new Promise<T>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    w.postMessage({ id, type, payload }, transfer || []);
  });
}

/**
 * Initialize Pyodide inside a dedicated Web Worker (non-blocking UI).
 */
export async function initPyodide(): Promise<void> {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      setStatus('loading', 'Starting Python worker...');
      await callWorker('init');
    } catch (e: any) {
      initPromise = null;
      setStatus('error', e.message || 'Failed to initialize Python runtime');
      throw e;
    }
  })();

  return initPromise;
}

/**
 * Pre-mount dataset file into Pyodide virtual filesystem (worker-side).
 */
export async function mountDatasetInVFS(filename: string, fileUrlOrData: string | Uint8Array) {
  await initPyodide();
  try {
    let content: Uint8Array;
    if (typeof fileUrlOrData === 'string') {
      if (fileUrlOrData.startsWith('/datasets/')) {
        const res = await fetch(fileUrlOrData);
        if (!res.ok) throw new Error(`HTTP ${res.status} fetching ${fileUrlOrData}`);
        content = new Uint8Array(await res.arrayBuffer());
      } else {
        content = new TextEncoder().encode(fileUrlOrData);
      }
    } else {
      content = fileUrlOrData;
    }

    // Transfer ownership of the buffer to the worker when possible
    const buffer = content.buffer.slice(content.byteOffset, content.byteOffset + content.byteLength);
    await callWorker('mount', { filename, content: new Uint8Array(buffer) }, [buffer]);
  } catch (e) {
    console.warn(`Could not mount ${filename}:`, e);
  }
}

/**
 * Reset Python notebook namespace (restart kernel equivalent)
 */
export async function resetPythonKernel() {
  await initPyodide();
  await callWorker('reset');
}

/**
 * Run Python Code or Cell safely in shared notebook global namespace (worker thread).
 */
export async function runPythonCode(code: string, resetGlobals = false): Promise<ExecutionResult> {
  await initPyodide();
  const startTime = performance.now();
  setBusy(1);

  try {
    const result = await callWorker<ExecutionResult>('run', { code, resetGlobals });
    return {
      stdout: result?.stdout || '',
      stderr: result?.stderr || '',
      error: result?.error || undefined,
      plots: Array.isArray(result?.plots) ? result.plots : [],
      executionTimeMs: result?.executionTimeMs ?? Math.round(performance.now() - startTime),
      timestamp: result?.timestamp || new Date().toISOString(),
    };
  } catch (err: any) {
    return {
      stdout: '',
      stderr: err.message || '',
      error: err.stack || String(err),
      plots: [],
      executionTimeMs: Math.round(performance.now() - startTime),
      timestamp: new Date().toISOString(),
    };
  } finally {
    setBusy(-1);
  }
}
